import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

export type PageSkeletonLayout = 'staff' | 'portal'

/** Which frame a page will open into, so the placeholder matches it. */
export function skeletonLayoutForPath(pathname: string): PageSkeletonLayout {
  return /^\/portal(?:\/|$)/.test(pathname) ? 'portal' : 'staff'
}

// Hairline-coloured shapes read on both the Cloud canvas and Snow cards; muted does not.
function Shape({ className, ...props }: React.ComponentProps<typeof Skeleton>) {
  return <Skeleton className={cn('bg-border motion-reduce:animate-none', className)} {...props} />
}

/**
 * Shown while a session check runs: the frame the page will open into (the staff
 * rail or the member portal header), a row of tiles and a table, in place of a
 * centred "Loading" line. Nothing authenticated is revealed until the check ends.
 */
export function PageSkeleton({ layout, label }: { layout: PageSkeletonLayout; label: string }) {
  return (
    <main className="min-h-screen bg-background" aria-busy="true">
      <p role="status" className="sr-only">
        {label}
      </p>
      <div
        aria-hidden="true"
        data-skeleton-layout={layout}
        className={layout === 'staff' ? 'flex min-h-screen' : undefined}
      >
        {layout === 'staff' ? <StaffRail /> : <PortalHeader />}
        <div
          className={cn(
            'min-w-0 flex-1',
            layout === 'portal' && 'mx-auto w-full max-w-3xl px-6 py-8',
          )}
        >
          {layout === 'staff' && (
            <div className="flex h-12 items-center border-b border-border px-4 md:hidden">
              <Shape className="size-7" />
            </div>
          )}
          <PageBody padded={layout === 'staff'} />
        </div>
      </div>
    </main>
  )
}

function StaffRail() {
  return (
    <div
      data-skeleton="sidebar"
      className="hidden w-64 shrink-0 space-y-6 border-r border-border bg-card p-4 md:block"
    >
      <Shape className="h-9 w-52 max-w-full" />
      <div className="space-y-4">
        {Array.from({ length: 8 }, (_, index) => (
          <Shape key={index} className="h-5 w-full" />
        ))}
      </div>
    </div>
  )
}

function PortalHeader() {
  return (
    <div data-skeleton="portal-header" className="border-b border-border bg-card">
      <div className="mx-auto flex h-16 max-w-screen-2xl items-center justify-between gap-4 px-6">
        <div className="flex items-center gap-3">
          <Shape className="size-8" />
          <Shape className="h-5 w-40" />
        </div>
        <Shape className="h-6 w-24" />
      </div>
      <div className="mx-auto flex max-w-screen-2xl gap-6 overflow-hidden px-6 py-3">
        {Array.from({ length: 6 }, (_, index) => (
          <Shape key={index} className="h-5 w-20 shrink-0" />
        ))}
      </div>
    </div>
  )
}

function PageBody({ padded }: { padded: boolean }) {
  return (
    <div className={cn('space-y-6', padded && 'mx-auto max-w-[1200px] px-6 py-6')}>
      <div className="space-y-2">
        <Shape className="h-6 w-64 max-w-full" />
        <Shape className="h-4 w-80 max-w-full" />
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <div
            key={index}
            data-skeleton="tile"
            className="h-24 space-y-3 rounded-md border border-border bg-card p-4"
          >
            <Shape className="h-4 w-24" />
            <Shape className="h-6 w-16" />
          </div>
        ))}
      </div>
      <div className="rounded-md border border-border bg-card">
        {Array.from({ length: 6 }, (_, index) => (
          <div
            key={index}
            data-skeleton="table-row"
            className="grid grid-cols-3 gap-6 border-t border-border px-4 py-3 first:border-t-0"
          >
            <Shape className="h-4 w-4/5" />
            <Shape className="h-4 w-full" />
            <Shape className="h-4 w-2/3" />
          </div>
        ))}
      </div>
    </div>
  )
}
